import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Users as UsersIcon, AlertTriangle, CheckCircle2, Shield, X, Copy, RefreshCw, Ban, Mail, KeyRound, ArrowLeft, Save } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";
import { PushToggle } from "../components/PushPermissionPrompt";

const ACCESS_LEVELS = [
  { value: "admin", label: "Admin" },
  { value: "contracts_manager", label: "Contracts Manager" },
  { value: "supervisor", label: "Supervisor" },
  { value: "competent_person", label: "Competent Person" },
  { value: "operative", label: "Operative" },
  { value: "subcontractor", label: "Subcontractor (thin time-entry)" },
];
const PAY_BASIS_OPTIONS = ["PAYE", "CIS-subbie"];
const OP_TYPES = ["Labourer", "Scaffolder"];
const SCAFFOLDER_GRADES = ["Part 1", "Part 2", "Advanced"];

const LOGIN_BADGE = {
  none: { bg: "#f1f5f9", fg: "#64748b", label: "NO LOGIN" },
  pending: { bg: "#fef3c7", fg: "#92400e", label: "INVITE PENDING" },
  active: { bg: "#dcfce7", fg: "#065f46", label: "ACTIVE" },
  active_with_pending_invite: { bg: "#dcfce7", fg: "#065f46", label: "ACTIVE · INVITE RESENT" },
  disabled: { bg: "#fee2e2", fg: "#b91c1c", label: "DISABLED" },
};

export default function People() {
  const { user: viewer } = useAuth();
  const canSeeEmailStatus = ["admin", "contracts_manager"].includes(viewer?.role);
  const [list, setList] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filter, setFilter] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/people"); setList(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadEmailStatus = async () => {
    if (!canSeeEmailStatus) return;  // gate the fetch — avoid a noisy 403 for supervisors/operatives
    try { const { data } = await api.get("/admin/email/status"); setEmailStatus(data); }
    catch { setEmailStatus(null); }
  };
  useEffect(() => { load(); loadEmailStatus(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (!filter) return list;
    const q = filter.toLowerCase();
    return list.filter((p) => (p.full_name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
  }, [list, filter]);

  if (selectedId) {
    return <PersonDetail personId={selectedId} onBack={() => { setSelectedId(null); load(); }} onSuccess={(m) => setSuccess(m)} onError={(m) => setError(m)} />;
  }

  return (
    <div data-testid="people-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>WORKFORCE · ONE RECORD PER PERSON · ACCESS GENERATED PER PERSON</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>People</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Newell staff (PAYE), self-employed CIS subbies, supervisors, admins. Existence of a record ≠ having access — use Generate access on a person&apos;s page to issue a single-use activation link.</div>
        {emailStatus ? (
          <div data-testid="email-status-badge" className="mono" style={{ display: "inline-block", marginTop: 10, padding: "6px 12px", borderRadius: 3, fontSize: 11, fontWeight: 700, background: emailStatus.ready ? "#dcfce7" : "#fef3c7", color: emailStatus.ready ? "#065f46" : "#92400e", maxWidth: "100%" }}>
            {emailStatus.ready
              ? `EMAIL DELIVERY · LIVE via ${emailStatus.sender_email}`
              : (
                <>
                  EMAIL DELIVERY · STUBBED — set on deployment:{" "}
                  {(emailStatus.missing || []).map((m, i) => (
                    <span key={m} style={{ background: "#fde68a", padding: "1px 6px", borderRadius: 2, marginLeft: i === 0 ? 4 : 4 }}>{m}</span>
                  ))}
                </>
              )}
          </div>
        ) : null}
        {emailStatus && (emailStatus.warnings || []).length ? (
          <div data-testid="email-status-warnings" style={{ marginTop: 8 }}>
            {emailStatus.warnings.map((w, i) => (
              <div key={i} className="card" style={{ padding: 8, background: "#fef3c7", borderLeft: "3px solid #f59e0b", color: "#92400e", fontSize: 12, marginTop: 4 }}>
                <strong style={{ marginRight: 6 }}>CONFIG WARNING:</strong>{w}
              </div>
            ))}
          </div>
        ) : null}
      </header>

      {error ? <Banner type="error" testid="people-error" msg={error} onClose={() => setError("")} /> : null}
      {success ? <Banner type="success" testid="people-success" msg={success} onClose={() => setSuccess("")} /> : null}

      <div className="card" style={{ padding: 12, marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <input data-testid="people-filter" className="input-base" placeholder="Filter by name or email…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 320 }} />
        <span className="mono" style={{ fontSize: 11, color: "#64748b", marginLeft: "auto" }}>{filtered.length} of {list.length}</span>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="people-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Access level</th>
              <th>Op type / grade</th>
              <th>RAMS auth.</th>
              <th>Pay basis</th>
              <th>Login</th>
              <th>Compliance</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No people match.</td></tr>
            ) : filtered.map((p) => {
              const b = LOGIN_BADGE[p.login_status] || LOGIN_BADGE.none;
              const rtwMiss = !p.rtw_held;
              const cisMiss = p.pay_basis === "CIS-subbie" && !p.cis_verified;
              return (
                <tr key={p.id} data-testid={`person-row-${p.id}`} onClick={() => setSelectedId(p.id)} style={{ cursor: "pointer" }}>
                  <td data-label="Name"><strong>{p.full_name}</strong>{p.preferred_name ? <span style={{ color: "#64748b", fontWeight: 400 }}> · {p.preferred_name}</span> : null}</td>
                  <td data-label="Email" style={{ fontSize: 12, color: "#64748b" }}>{p.email || <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                  <td data-label="Access" style={{ fontSize: 12 }}>{p.access_level || <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                  <td data-label="Type/Grade" style={{ fontSize: 12 }}>{p.op_type}{p.scaffolder_grade ? ` · ${p.scaffolder_grade}` : ""}</td>
                  <td data-label="RAMS">{p.rams_signoff_authority ? <span className="mono" style={{ background: "#dcfce7", color: "#065f46", padding: "1px 6px", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>YES</span> : <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>}</td>
                  <td data-label="Pay" style={{ fontSize: 12 }}>{p.pay_basis}</td>
                  <td data-label="Login"><span data-testid={`person-login-${p.id}`} className="mono" style={{ background: b.bg, color: b.fg, padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700 }}>{b.label}</span></td>
                  <td data-label="Compliance">
                    {rtwMiss ? <span className="mono" style={{ background: "#fee2e2", color: "#b91c1c", padding: "1px 5px", borderRadius: 2, fontSize: 10, fontWeight: 700, marginRight: 4 }}>RTW</span> : null}
                    {cisMiss ? <span className="mono" style={{ background: "#fee2e2", color: "#b91c1c", padding: "1px 5px", borderRadius: 2, fontSize: 10, fontWeight: 700 }}>CIS</span> : null}
                    {!rtwMiss && !cisMiss ? <span style={{ color: "#cbd5e1", fontSize: 10 }}>✓</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PersonDetail({ personId, onBack, onSuccess, onError }) {
  const { user: viewer } = useAuth();
  const canManage = ["admin", "contracts_manager"].includes(viewer?.role);
  const [p, setP] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [accessLevel, setAccessLevel] = useState("operative");
  const [activationLink, setActivationLink] = useState("");
  const [linkExpires, setLinkExpires] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/people/${personId}`);
      setP(data);
      setAccessLevel(data.access_level || "operative");
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => {
    load();
    api.get("/subcontractor-companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));
    // eslint-disable-next-line
  }, [personId]);

  if (!p) return <div style={{ padding: 24 }}>Loading…</div>;

  const isCIS = p.pay_basis === "CIS-subbie";
  const setField = (k, v) => setP({ ...p, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/operatives/${p.id}`, p);
      onSuccess(`Saved ${p.full_name}`);
      load();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const generateAccess = async () => {
    if (!p.email) { onError("Email address required before generating access"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/people/${p.id}/generate-access`, { access_level: accessLevel });
      setActivationLink(data.activation_link);
      setLinkExpires(data.expires_at);
      onSuccess("Access generated — copy the link below and send it to the person.");
      load();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resendInvite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/people/${p.id}/resend-invite`);
      setActivationLink(data.activation_link);
      setLinkExpires(data.expires_at);
      onSuccess("Invite resent — copy the new link below.");
      load();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeAccess = async () => {
    if (!window.confirm("Revoke this person's login? They will be immediately signed out and unable to log back in.")) return;
    setBusy(true);
    try {
      await api.post(`/people/${p.id}/revoke-access`);
      onSuccess("Access revoked");
      setActivationLink("");
      load();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    const fullLink = activationLink.startsWith("http") ? activationLink : `${window.location.origin}${activationLink}`;
    try { await navigator.clipboard.writeText(fullLink); onSuccess("Link copied to clipboard"); }
    catch { window.prompt("Copy this link:", fullLink); }
  };

  const b = LOGIN_BADGE[p.login_status] || LOGIN_BADGE.none;

  return (
    <div data-testid="person-detail" style={{ padding: "24px 24px 64px" }}>
      <button data-testid="back-to-people" className="btn-ghost" onClick={onBack}><ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Back to People</button>

      <header style={{ margin: "12px 0 20px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>{p.full_name}</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>
            <span className="mono" style={{ background: b.bg, color: b.fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700, marginRight: 8 }}>{b.label}</span>
            {p.access_level ? <span className="mono" style={{ fontSize: 11 }}>access: {p.access_level}</span> : null}
          </div>
        </div>
        <div>
          <button data-testid="save-person" className="btn-primary" onClick={save} disabled={busy}><Save size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Save changes</button>
        </div>
      </header>

      {/* DEVICE-LEVEL PUSH TOGGLE — only when viewing own profile */}
      {p.linked_user_id === viewer?.id ? (
        <div style={{ marginBottom: 12 }}>
          <PushToggle />
        </div>
      ) : null}

      {/* ACCESS GENERATOR */}
      <Card title={<><KeyRound size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />Access generator</>}>        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, alignItems: "end" }}>
          <Field label="Email (required for access)">
            <input data-testid="person-email" className="input-base" type="email" value={p.email || ""} onChange={(e) => setField("email", e.target.value)} />
          </Field>
          <Field label="Access level">
            <select data-testid="person-access-level" className="input-base" value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}>
              {ACCESS_LEVELS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {canManage && p.login_status === "none" ? (
              <button data-testid="generate-access-btn" className="btn-primary" onClick={generateAccess} disabled={busy || !p.email}><Mail size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Generate access</button>
            ) : canManage ? (
              <>
                <button data-testid="resend-invite-btn" className="btn-ghost" onClick={resendInvite} disabled={busy}><RefreshCw size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Resend invite / Reset password</button>
                <button data-testid="revoke-access-btn" className="btn-ghost" style={{ color: "#b91c1c" }} onClick={revokeAccess} disabled={busy}><Ban size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Revoke</button>
              </>
            ) : (
              <div className="mono" style={{ fontSize: 11, color: "#64748b", padding: "8px 4px" }}>Read-only · admin or contracts manager can manage access</div>
            )}
          </div>
        </div>
        {activationLink ? (
          <div data-testid="activation-link-card" className="card" style={{ marginTop: 12, padding: 12, background: "#fff7ed", border: "1px solid #fdba74" }}>
            <div className="mono" style={{ fontSize: 11, color: "#9a3412", fontWeight: 700, marginBottom: 4 }}>SINGLE-USE ACTIVATION LINK · EXPIRES {fmtDateTime(linkExpires)}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input data-testid="activation-link-input" readOnly value={activationLink.startsWith("http") ? activationLink : `${window.location.origin}${activationLink}`} className="input-base mono" style={{ flex: 1, fontSize: 11 }} />
              <button data-testid="copy-activation-link" className="btn-primary" onClick={copyLink}><Copy size={14} /></button>
            </div>
            <div className="mono" style={{ fontSize: 10, color: "#9a3412", marginTop: 6 }}>Email delivery is stubbed — copy this and send manually. When notifications are live, the link will be emailed automatically.</div>
          </div>
        ) : null}
      </Card>

      {/* IDENTITY & EMPLOYMENT */}
      <Card title="Identity, contact & employment">
        <Two>
          <Field label="Full legal name"><input className="input-base" value={p.full_name || ""} onChange={(e) => setField("full_name", e.target.value)} /></Field>
          <Field label="Preferred name"><input className="input-base" value={p.preferred_name || ""} onChange={(e) => setField("preferred_name", e.target.value)} /></Field>
          <Field label="Personal phone"><input data-testid="person-phone" className="input-base mono" value={p.contact_number || ""} onChange={(e) => setField("contact_number", e.target.value)} /></Field>
          <Field label="Start date"><input className="input-base" type="date" value={p.start_date || ""} onChange={(e) => setField("start_date", e.target.value)} /></Field>
          <Field label="Status"><select className="input-base" value={p.status} onChange={(e) => setField("status", e.target.value)}><option>active</option><option>inactive</option></select></Field>
          <Field label="Pay basis"><select data-testid="person-pay-basis" className="input-base" value={p.pay_basis} onChange={(e) => setField("pay_basis", e.target.value)}>{PAY_BASIS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</select></Field>
          <Field label="Hourly rate (£)"><input className="input-base" type="number" value={p.hourly_rate || 0} onChange={(e) => setField("hourly_rate", parseFloat(e.target.value) || 0)} /></Field>
          <Field label="Supplied by company"><select className="input-base" value={p.subcontractor_company_id || ""} onChange={(e) => setField("subcontractor_company_id", e.target.value || null)}><option value="">— direct hire / self-employed —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        </Two>
      </Card>

      {/* COMPETENCE — THE 3 SEPARATE FIELDS */}
      <Card title="Competence & sign-off (kept separate from access level)" subtitle="Access level controls what they can do in the app · Op type / grade is real-world competence · RAMS sign-off is admin-set, separate from both.">
        <Two>
          <Field label="Operative type"><select data-testid="person-op-type" className="input-base" value={p.op_type || "Labourer"} onChange={(e) => setField("op_type", e.target.value)}>{OP_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          {p.op_type === "Scaffolder" ? (
            <Field label="Scaffolder grade"><select data-testid="person-grade" className="input-base" value={p.scaffolder_grade || "Part 1"} onChange={(e) => setField("scaffolder_grade", e.target.value)}>{SCAFFOLDER_GRADES.map((g) => <option key={g}>{g}</option>)}</select></Field>
          ) : <div />}
          <Field label="CISRS number"><input className="input-base mono" value={p.cisrs_number || ""} onChange={(e) => setField("cisrs_number", e.target.value)} /></Field>
          <Field label="CISRS expiry"><input className="input-base" type="date" value={p.cisrs_expiry || ""} onChange={(e) => setField("cisrs_expiry", e.target.value)} /></Field>
          <Field label="RAMS sign-off authority">
            <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
              <input data-testid="person-rams-auth" type="checkbox" checked={!!p.rams_signoff_authority} onChange={(e) => setField("rams_signoff_authority", e.target.checked)} style={{ width: 16, height: 16 }} />
              <span style={{ fontWeight: 700, color: p.rams_signoff_authority ? "#065f46" : "#64748b" }}>{p.rams_signoff_authority ? "YES — can sign off RAMS" : "No"}</span>
            </label>
          </Field>
          <Field label="Competence basis (if RAMS auth.)"><input className="input-base" value={p.competence_basis || ""} onChange={(e) => setField("competence_basis", e.target.value)} placeholder="e.g. CISRS Advanced + Newell-internal sign-off" /></Field>
        </Two>
      </Card>

      {/* RTW — HARD GATE */}
      <Card title={<><Shield size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />Right to work (hard gate — required before deployment)</>}>
        <Two>
          <Field label="RTW held?">
            <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
              <input data-testid="person-rtw" type="checkbox" checked={!!p.rtw_held} onChange={(e) => setField("rtw_held", e.target.checked)} style={{ width: 16, height: 16 }} />
              <span style={{ fontWeight: 700, color: p.rtw_held ? "#065f46" : "#b91c1c" }}>{p.rtw_held ? "HELD" : "NOT HELD"}</span>
            </label>
          </Field>
          <Field label="Check date"><input className="input-base" type="date" value={p.rtw_check_date || ""} onChange={(e) => setField("rtw_check_date", e.target.value)} /></Field>
          <Field label="Method"><select className="input-base" value={p.rtw_method || ""} onChange={(e) => setField("rtw_method", e.target.value)}><option value="">—</option><option value="passport">Passport</option><option value="share-code">Online share code</option><option value="brp">BRP</option><option value="other">Other</option></select></Field>
          <Field label="Share code"><input className="input-base mono" value={p.rtw_share_code || ""} onChange={(e) => setField("rtw_share_code", e.target.value)} /></Field>
          <Field label="RTW expiry (if time-limited)"><input className="input-base" type="date" value={p.rtw_expiry || ""} onChange={(e) => setField("rtw_expiry", e.target.value)} /></Field>
          <Field label="RTW document URL"><input className="input-base mono" value={p.rtw_document_url || ""} onChange={(e) => setField("rtw_document_url", e.target.value)} /></Field>
        </Two>
      </Card>

      {/* CIS — HARD GATE FOR PAY */}
      {isCIS ? (
        <Card title="CIS / Tax (hard gate on Xero export until verified)" highlight>
          <Two>
            <Field label="NI number"><input data-testid="person-ni" className="input-base mono" value={p.ni_number || ""} onChange={(e) => setField("ni_number", e.target.value)} /></Field>
            <Field label="UTR"><input data-testid="person-utr" className="input-base mono" value={p.utr || ""} onChange={(e) => setField("utr", e.target.value)} /></Field>
            <Field label="CIS verified with HMRC?">
              <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
                <input data-testid="person-cis-verified" type="checkbox" checked={!!p.cis_verified} onChange={(e) => setField("cis_verified", e.target.checked)} style={{ width: 16, height: 16 }} />
                <span style={{ fontWeight: 700, color: p.cis_verified ? "#065f46" : "#b91c1c" }}>{p.cis_verified ? "VERIFIED" : "NOT VERIFIED"}</span>
              </label>
            </Field>
            <Field label="HMRC reference"><input className="input-base mono" value={p.cis_hmrc_reference || ""} onChange={(e) => setField("cis_hmrc_reference", e.target.value)} /></Field>
            <Field label="Deduction rate (%)"><select className="input-base" value={p.cis_deduction_rate ?? ""} onChange={(e) => setField("cis_deduction_rate", e.target.value === "" ? null : Number(e.target.value))}><option value="">—</option><option value="0">0% (gross)</option><option value="20">20%</option><option value="30">30%</option></select></Field>
          </Two>
        </Card>
      ) : (
        <Card title="PAYE tax">
          <Two>
            <Field label="Tax code"><input className="input-base mono" value={p.tax_code || ""} onChange={(e) => setField("tax_code", e.target.value)} placeholder="e.g. 1257L" /></Field>
            <Field label="NI number"><input className="input-base mono" value={p.ni_number || ""} onChange={(e) => setField("ni_number", e.target.value)} /></Field>
          </Two>
        </Card>
      )}

      {/* ADDRESS, PERSONAL, INDUCTIONS */}
      <Card title="Address & personal (admin-only)">
        <Two>
          <Field label="Date of birth"><input className="input-base" type="date" value={p.dob || ""} onChange={(e) => setField("dob", e.target.value)} /></Field>
          <Field label="Home address"><input className="input-base" value={p.home_address || p.address || ""} onChange={(e) => setField("home_address", e.target.value)} /></Field>
          <Field label="Postcode"><input className="input-base mono" value={p.postcode || ""} onChange={(e) => setField("postcode", e.target.value)} /></Field>
          <Field label="Photo ID type"><select className="input-base" value={p.photo_id_type || ""} onChange={(e) => setField("photo_id_type", e.target.value)}><option value="">—</option><option>Passport</option><option>Driving licence</option></select></Field>
          <Field label="Photo ID URL"><input className="input-base mono" value={p.photo_id_url || ""} onChange={(e) => setField("photo_id_url", e.target.value)} /></Field>
          <Field label="Address proof URL"><input className="input-base mono" value={p.address_proof_url || ""} onChange={(e) => setField("address_proof_url", e.target.value)} /></Field>
        </Two>
      </Card>

      <Card title="Next of kin & health (admin-only)">
        <Two>
          <Field label="Next of kin name"><input className="input-base" value={p.next_of_kin_name || ""} onChange={(e) => setField("next_of_kin_name", e.target.value)} /></Field>
          <Field label="Relationship"><input className="input-base" value={p.next_of_kin_relationship || ""} onChange={(e) => setField("next_of_kin_relationship", e.target.value)} /></Field>
          <Field label="Phone"><input className="input-base mono" value={p.next_of_kin_phone || ""} onChange={(e) => setField("next_of_kin_phone", e.target.value)} /></Field>
          <Field label="Fitness / medical notes (work at height)"><textarea className="input-base" rows={2} value={p.medical_notes || ""} onChange={(e) => setField("medical_notes", e.target.value)} /></Field>
        </Two>
      </Card>

      <Card title="Inductions & DBS">
        <Two>
          <Field label="Newell food-environment induction"><input className="input-base" type="date" value={p.newell_induction_date || ""} onChange={(e) => setField("newell_induction_date", e.target.value)} /></Field>
          <Field label="McCain site induction"><input className="input-base" type="date" value={p.mccain_induction_date || ""} onChange={(e) => setField("mccain_induction_date", e.target.value)} /></Field>
          <Field label="DBS required?">
            <label className="mono" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 0" }}>
              <input type="checkbox" checked={!!p.dbs_required} onChange={(e) => setField("dbs_required", e.target.checked)} style={{ width: 16, height: 16 }} />
              <span>{p.dbs_required ? "Required" : "—"}</span>
            </label>
          </Field>
          {p.dbs_required ? (<>
            <Field label="DBS number"><input className="input-base mono" value={p.dbs_number || ""} onChange={(e) => setField("dbs_number", e.target.value)} /></Field>
            <Field label="DBS expiry"><input className="input-base" type="date" value={p.dbs_expiry || ""} onChange={(e) => setField("dbs_expiry", e.target.value)} /></Field>
          </>) : null}
        </Two>
      </Card>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button className="btn-primary" onClick={save} disabled={busy}><Save size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function Banner({ type, msg, testid, onClose }) {
  const bg = type === "error" ? "#fef2f2" : "#ecfdf5";
  const fg = type === "error" ? "#b91c1c" : "#065f46";
  const border = type === "error" ? "#dc2626" : "#059669";
  const Icon = type === "error" ? AlertTriangle : CheckCircle2;
  return (
    <div data-testid={testid} className="card" style={{ padding: 12, marginBottom: 12, background: bg, color: fg, borderLeft: `4px solid ${border}`, display: "flex", gap: 8, alignItems: "center" }}>
      <Icon size={16} /> <span style={{ flex: 1 }}>{msg}</span>
      {onClose ? <button className="btn-ghost" onClick={onClose}><X size={14} /></button> : null}
    </div>
  );
}

function Card({ title, subtitle, children, highlight }) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 12, background: highlight ? "#fffbeb" : "white", border: `1px solid ${highlight ? "#fde68a" : "#e2e8f0"}` }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontWeight: 900, fontSize: 16 }}>{title}</h3>
        {subtitle ? <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{subtitle}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Two({ children }) { return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>; }

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div className="mono" style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
